'use client'
import { type FC, memo, useCallback } from 'react'
import styled from 'styled-components'
import { mq, vw,  presets } from '@/styles'
// import { Lottie } from './Lottie'
import type { TypeDictionaryKeynames } from '@/app/dictionaries'
import { useTranslator } from '@/hooks'
import { useRouter } from 'next/navigation'
import { Button } from './Button'
import { HeaderMarquee } from '../project/HeaderMarquee'

type notFoundPageType = 'generic' |'category' | 'brand' | 'product' | 'order'
type notAvailablePageType = 'categories' | 'orders' | 'invoices' | 'my_products'

type TypeNotFoundProps = { pageType?: notFoundPageType, type?: 'not_found' }
type TypeNotAvailableProps = { pageType?: notAvailablePageType, type?: 'not_available' }

/**
 * @param pageType - The type should match the project dictionaries json files
 * @param type - The type should match the project dictionaries json files
 */
type Props = TypeNotFoundProps | TypeNotAvailableProps



type TypeGetTextsParams = {
	t: (keyValue: TypeDictionaryKeynames) => string | number | boolean | (string | number | boolean)[]
	pageType: Props['pageType']
	type: Props['type']
}
const getTexts = ({ t, pageType, type }: TypeGetTextsParams) => {
	if (!t) return { 
		title: t('project.not_found.generic.title') as string,
		description: t('project.not_found.generic.description') as string, 
		ctaText: t('project.not_found.generic.cta.text') as string,
		ctaHref: t('project.not_found.generic.cta.href') as string,
	}

	if (type === 'not_found') {
		const title = t(`project.${type}.${pageType as notFoundPageType}.title`) as string
		const description = t(`project.${type}.${pageType as notFoundPageType}.description`) as string
		const ctaText = t(`project.${type}.${pageType as notFoundPageType}.cta.text`) as string
		const ctaHref = t(`project.${type}.${pageType as notFoundPageType}.cta.href`) as string
		return { title, description, ctaText, ctaHref }
	}

	if (type === 'not_available') {
		const title = t(`project.${type}.${pageType as notAvailablePageType}.title`) as string
		const description = t(`project.${type}.${pageType as notAvailablePageType}.description`) as string
		const ctaText = t(`project.${type}.${pageType as notAvailablePageType}.cta.text`) as string
		const ctaHref = t(`project.${type}.${pageType as notAvailablePageType}.cta.href`) as string
		return { title, description, ctaText, ctaHref }
	}
}

export const NotFoundPage:FC<Props> = memo(({ pageType = 'generic', type = 'not_found' }) => {
  const { t } = useTranslator()
  const { push } = useRouter()

	const { title, description, ctaText, ctaHref } = getTexts({ t, pageType, type }) || { title: '404', description: '', ctaText: '', ctaHref: '/' }

	const onClick = useCallback(() => {
    push(ctaHref ?? '/')
  }, [push, ctaHref])

  return (
    <Main className={`${pageType} ${type}`}>
      {pageType === 'generic' && <HeaderMarquee data={title} />}
      <Section>
        <hgroup>
          {/* <Lottie name='404' isActive={true} /> */}
          <h1>{title}</h1>
          <p>{description}</p>
          <Button onClick={onClick} bgColor='--color-text--yellow'>{ctaText}</Button>
        </hgroup>
      </Section>
    </Main>
  )
})

export const Main = styled.main`
  flex-grow: 2;
	grid-column: 1 / span 6;
  overflow-x: hidden;
  padding: 0;
  position: relative;
	
  ${mq.greaterThan('tablet')} {
		grid-column: 4 / span 6;
    overflow-x: inherit;
  }

	&.my_products {
		~ section {
			display: none !important;
		}
	}

	&.category,
	&.product,
	&.brand {
		~ section {
			display: none !important;
		}
	}
`

export const Section = styled.section`
  grid-column: 1 / span 6;
  padding: ${vw(50, 'mobile')} ${vw(20, 'mobile')};
  align-items: center;
  margin-bottom:${vw(50, 'mobile')} ;

  ${mq.greaterThan('tablet')} {
    padding: ${vw(50, 'tablet')} ${vw(20, 'tablet')};
    margin-bottom:${vw(50, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    grid-column: 5 / span 4;
    padding: ${vw(60, 'desktop')} 0;
    margin-bottom:${vw(60, 'desktop')} ;
  }

  ${mq.greaterThan('desktop')} {
    padding: 60px 0;
    margin-bottom: 60px;
  }

  ${mq.greaterThan('container')} {
    margin: 0 auto;
    max-width: var(--page-container-max-width);
  }

  button {
    color: var(--color-background);
    margin-top: ${vw(60, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-top:${vw(60, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-top:${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-top: 30px;
    }
  }

  h1, p {
    padding: 15px;
    text-align: center;
  }

  h1 {
    ${presets.font26()}
    color: var(--color-text);
    font-family: var(--font-basier-circle);
  }

  p {
    ${presets.font18_1()}
    color:  var(--color-text--darker);
  }
`
